import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { Layers } from 'lucide-react';
import { DataTable, DataTablePagination } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { Button } from '@/components/ui/button';
import { useKeepPageInRange } from '@/hooks/useKeepPageInRange';
import { cn } from '@/lib/utils';
import { formatNumber, formatTaka, pluralize } from '@/utils/number';
import { pcmpAccountsBatchesQueryOptions } from '../../api/pcmpAccounts.api';
import {
  PCMP_ACCOUNTS_BATCH_VIEW_LABELS,
  PCMP_ACCOUNTS_EMPTY_MESSAGE,
  PCMP_ACCOUNTS_ROLL_UP_TONE,
} from '../../constants/pcmpAccounts.constants';
import { PCMP_QUEUE_PAGE_SIZE_OPTIONS } from '../../constants/pcmpQueue.constants';
import type { PcmpAccountsFilterController } from '../../hooks/usePcmpAccountsFilters';
import { toPcmpQueueSearch } from '../../hooks/usePcmpQueueFilters';
import {
  PCMP_ACCOUNTS_BATCH_VIEWS,
  type PcmpAccountsBatch,
  type PcmpAccountsBatches,
  type PcmpAccountsBatchKind,
  type PcmpAccountsBatchView,
} from '../../types/pcmpAccounts.types';
import { pcmpSegmentClass } from '../../utils/pcmpSegmentClass';
import {
  PcmpBatchAmountCell,
  PcmpBatchItemsCell,
  PcmpBatchStepCell,
} from '../PcmpBatch/PcmpBatchCells';
import { PcmpStatusPill } from '../PcmpProposalDetail/PcmpStatusPill';

const amountColumn = (
  id: 'pending' | 'disbursed' | 'disbursementFailed' | 'returned' | 'held',
  label: string,
): ColumnDef<PcmpAccountsBatch> => ({
  id,
  header: () => <div className="text-right">{label}</div>,
  cell: ({ row }) => <PcmpBatchAmountCell value={row.original[id]} />,
});

const columns: ColumnDef<PcmpAccountsBatch>[] = [
  {
    id: 'batchNo',
    header: 'Batch',
    cell: ({ row }) => (
      <span className="font-mono font-medium whitespace-nowrap">#{row.original.batchNo}</span>
    ),
  },
  {
    id: 'rollUp',
    header: 'Status',
    cell: ({ row }) => (
      <PcmpStatusPill tone={PCMP_ACCOUNTS_ROLL_UP_TONE[row.original.rollUp]}>
        {row.original.rollUpLabel}
      </PcmpStatusPill>
    ),
  },
  {
    id: 'items',
    header: () => <div className="text-right">Proposals</div>,
    cell: ({ row }) => (
      <div className="text-right">
        <PcmpBatchItemsCell batch={row.original} />
      </div>
    ),
  },
  {
    id: 'amount',
    header: () => <div className="text-right">Amount</div>,
    cell: ({ row }) => (
      <div className="text-right whitespace-nowrap tabular-nums">
        {formatTaka(row.original.totalAmount)}
      </div>
    ),
  },
  amountColumn('pending', 'Pending'),
  amountColumn('disbursed', 'Disbursed'),
  amountColumn('disbursementFailed', 'Failed'),
  amountColumn('returned', 'Returned'),
  amountColumn('held', 'On Hold'),
  {
    id: 'published',
    header: 'Published',
    cell: ({ row }) => (
      <PcmpBatchStepCell by={row.original.publishedBy} at={row.original.publishedAt} />
    ),
  },
];

function TotalsStrip({ totals }: { totals: PcmpAccountsBatches['totals'] }) {
  const items = [
    {
      label: 'All batches',
      amount: totals.totalAmount,
      note: `${formatNumber(totals.batches)} ${pluralize(totals.batches, 'batch', 'batches')} · ${formatNumber(totals.itemCount)} ${pluralize(totals.itemCount, 'proposal')}`,
    },
    ...(
      [
        ['Pending', totals.pending],
        ['Disbursed', totals.disbursed],
        ['Failed', totals.disbursementFailed],
        ['Returned', totals.returned],
        ['On Hold', totals.held],
      ] as const
    ).map(([label, value]) => ({
      label,
      amount: value.amount,
      note: `${formatNumber(value.count)} ${pluralize(value.count, 'proposal')}`,
    })),
  ];
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
      {items.map(({ label, amount, note }) => (
        <div key={label} className="flex flex-col rounded-xl border bg-card px-4 py-3 shadow-sm">
          <span className="text-xs text-muted-foreground">{label}</span>
          <span className="text-lg font-semibold tabular-nums">{formatTaka(amount)}</span>
          <span className="text-xs text-muted-foreground tabular-nums">{note}</span>
        </div>
      ))}
    </div>
  );
}

interface PcmpAccountsBatchesViewProps {
  controller: PcmpAccountsFilterController;
  batchKind: PcmpAccountsBatchKind;
  view: PcmpAccountsBatchView;
}

/** MIS (route) or urgent batches holding proposals routed to Accounts; a row opens the batch. */
export function PcmpAccountsBatchesView({
  controller,
  batchKind,
  view,
}: PcmpAccountsBatchesViewProps) {
  const router = useRouter();
  const { tab, pageParams, appliedFilters, limit } = controller;

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    pcmpAccountsBatchesQueryOptions({ ...pageParams, kind: batchKind, view }),
  );

  useKeepPageInRange(pageParams.page, data?.batches.meta.totalPages, controller.setPage);

  const openBatch = useCallback(
    (batch: PcmpAccountsBatch) => {
      void router.navigate({
        to: '/pcmp/accounts/batches/$batchNo',
        params: { batchNo: batch.batchNo },
        // The batch opens on the members matching the same filters.
        search: toPcmpQueueSearch(appliedFilters),
      });
    },
    [router, appliedFilters],
  );

  return (
    <>
      {batchKind === 'URGENT' ? (
        <div className="flex w-full max-w-full items-center overflow-x-auto rounded-lg border bg-muted/40 p-1 sm:w-fit">
          {PCMP_ACCOUNTS_BATCH_VIEWS.map((option) => (
            <Button
              key={option}
              type="button"
              variant="ghost"
              size="sm"
              aria-pressed={view === option}
              onClick={() => controller.setUrgentView(option)}
              className={pcmpSegmentClass(view === option)}
            >
              {PCMP_ACCOUNTS_BATCH_VIEW_LABELS[option]}
            </Button>
          ))}
        </div>
      ) : null}

      {error ? (
        <ErrorState
          error={error}
          fallbackTitle="Could not load the batches"
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          fill
        />
      ) : (
        <>
          {data ? <TotalsStrip totals={data.totals} /> : null}
          <DataTable
            data={data?.batches.items ?? []}
            columns={columns}
            isLoading={isLoading}
            fillHeight
            className={cn('lg:min-h-0 lg:flex-1', isFetching && !isLoading && 'opacity-70')}
            emptyIcon={Layers}
            emptyMessage={PCMP_ACCOUNTS_EMPTY_MESSAGE[tab]}
            skeletonRowCount={Math.min(limit, 15)}
            onRowClick={openBatch}
          />
          {data ? (
            <DataTablePagination
              page={data.batches.meta.page}
              totalPages={data.batches.meta.totalPages}
              total={data.batches.meta.total}
              limit={data.batches.meta.limit}
              onPageChange={controller.setPage}
              limitSelect={{ options: PCMP_QUEUE_PAGE_SIZE_OPTIONS, onChange: controller.setLimit }}
            />
          ) : null}
        </>
      )}
    </>
  );
}
